/**
 * Lazy-load the header drawer (burger menu) contents.
 *
 * Sep 2026 SEO recovery. The drawer duplicated every navigation link plus the
 * 236-country flag list on every page (~5.5k DOM nodes, ~500 KB of HTML), which
 * was a large part of the ~20k-node pages that followed the May 2026 migration.
 *
 * snippets/header-drawer.liquid now renders only the shell (burger button, empty
 * panel with a "Loading..." line). On the first user interaction this script
 * fetches the SAME header section through the Section Rendering API, on the
 * search path with a marker term so sections/header.liquid renders the drawer in
 * full (see `lazy_drawer` there), and moves the contents into the shell. Lab
 * tools and crawlers never interact, so they keep the small DOM; real users get
 * the menu within a few hundred ms of their first touch/pointer/key, normally
 * long before they tap the burger.
 */
(function () {
  'use strict';

  var container = document.querySelector('header-drawer [data-lazy-drawer="pending"]');
  if (!container) return;

  var headerSection = container.closest('.shopify-section');
  if (!headerSection || !headerSection.id) return;
  var sectionId = headerSection.id.replace(/^shopify-section-/, '');
  var drawer = container.closest('header-drawer');
  var details = container.closest('details');
  var started = false;

  function ensureLocalizationScript() {
    // theme.liquid loads localization-form.js via <lazy-script data-watch-element="localization-form">,
    // which only observes elements present at page load. The selector arrives with the drawer, so load it here.
    if (customElements.get('localization-form')) return;
    if (!container.querySelector('localization-form')) return;
    var src = container.getAttribute('data-localization-script');
    if (!src || document.querySelector('script[src="' + src + '"]')) return;
    var script = document.createElement('script');
    script.src = src;
    script.defer = true;
    document.head.appendChild(script);
  }

  function bind() {
    if (!drawer || typeof drawer.onSummaryClick !== 'function') return;
    // Mirror HeaderDrawer.bindEvents() for the injected nodes only (the burger summary is already bound).
    container.querySelectorAll('summary').forEach(function (summary) {
      summary.addEventListener('click', drawer.onSummaryClick.bind(drawer));
    });
    container
      .querySelectorAll('button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)')
      .forEach(function (button) {
        button.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
      });
  }

  function load() {
    if (started) return;
    started = true;

    var url = '/search?q=lazy-header-drawer&section_id=' + encodeURIComponent(sectionId);
    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('header-drawer .menu-drawer__navigation-container');
        if (!source) throw new Error('drawer content not found in section response');

        var nodes = Array.prototype.slice.call(source.childNodes);
        container.replaceChildren.apply(container, nodes);
        container.setAttribute('data-lazy-drawer', 'loaded');

        bind();
        ensureLocalizationScript();
        document.dispatchEvent(new CustomEvent('header-drawer:loaded'));
      })
      .catch(function (error) {
        started = false;
        container.setAttribute('data-lazy-drawer', 'error');
        if (window.console) console.warn('[header-lazy-drawer]', error);
      });
  }

  var options = { once: true, passive: true };
  ['touchstart', 'pointerdown', 'pointermove', 'keydown'].forEach(function (type) {
    window.addEventListener(type, load, options);
  });
  if (details) {
    details.addEventListener('toggle', function () {
      if (details.open) load();
    });
  }
})();
